import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import RouteManager from "../../../managers/RouteManager";
import AppManager from "../../../managers/AppManager";
import {
    DKLabel,
    DKButton,
    DKLine,
    DKInput,
    INPUT_VIEW_DIRECTION,
    showToast,
    TOAST_TYPE,
    showLoader,
    removeLoader,
    DKCheckMark
} from "deskera-ui-library";
import { BUSINESS_TYPE, FEDERAL_DEPOSIT_SCHEDULE, FEDERAL_FILTER_TYPE } from "./ConstantsUS";
import TaxService from "../../../services/compliance/TaxSerivce";
import Table from "../../../services/table";
import { COUNTRY } from "../../../constants/CountryConstant";
import TenantManager from "../../../managers/TenantManager";
import ProfileSetupService from "../../../services/ProfileSetup-service";
import { PROFILE_STAGES_CONSTANT } from "../../../constants/Constant";
import { TENANT, TenantService } from "../../../managers/TenantManagerService";
import { removeDelayedLoader, showDelayedLoader } from "../../../utility/Utility";
import {INPUT_TYPE} from "../../../constants/Constant"
import PermissionUtils, { ACTION, MODULE } from "../../user-permissions/PermissionUtils";

function USTaxDetailsNew(props) {
    const history = useHistory();
    const { t } = useTranslation();

    const initialFederalFields = [
        {
            id: "federalEmployeeIdNo",
            title: t("FEIN"),
            type: INPUT_TYPE.NUMBER,
            validation: [["required"]]
        },
        {
            id: "businessType",
            title: t("BUSINESS_TYPE"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]],
            options: BUSINESS_TYPE
        },
        {
            id: "federalFilterType",
            title: t("FEDERAL_FILTER_TYPE"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]],
            options: FEDERAL_FILTER_TYPE
        },
        {
            id: "federalDepositSchedule",
            title: t("FEDERAL_DEPOSIT_SCHEDULE"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]],
            options: FEDERAL_DEPOSIT_SCHEDULE
        }
    ];

    const [federalFields, setFederalFields] = useState(initialFederalFields);
    const [stateFields, setStateFields] = useState([]);
    const [taxDetails, setTaxDetails] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [stateCode] = useState(TenantService.get(TENANT.STATE_CODE));

    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        if (!props.renderAsStep) {
            AppManager.scrollToTop();
        }

        getStateFieldsAndTaxDetails();
    }, []);

    useEffect(() => {
        if (taxDetails) {
            setTaxDetailsFieldData();
        }
    }, [taxDetails]);

    const getStateFieldsAndTaxDetails = async () => {
        const timeout = showDelayedLoader();

        if (stateCode) {
            if(TenantService.get(TENANT.IS_ADMIN) || PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.VIEW])) {
                const stateConfig = await Table.getStateFormsAndFields(COUNTRY.US, stateCode);
                setTaxField(stateConfig)
            } else {
                const stateConfig = await Table.getStateFormsAndFieldsEmployee(COUNTRY.US, stateCode);
                setTaxField(stateConfig)
            }
        }
        const taxDetails = await TaxService.getTaxDetail(COUNTRY.US);
        setTaxDetailsData(taxDetails);

        removeDelayedLoader(timeout);
    }

    const setTaxField = (stateConfig)=>{
        if (Array.isArray(stateConfig?.sections)) {
            setStateTaxFields(stateConfig.sections[0]?.fields || []);
        }
    }
    const setStateTaxFields = (fields) => {
        if (Array.isArray(fields)) {
            const stateFields = fields.map((field) => {
                let defaultValue = field.defaultValue;
                let options = null;

                if (field.fieldType === INPUT_TYPE.DROPDOWN) {
                    options = getDropdownOptions(field.options);
                    defaultValue = getDropdownLabel(field.defaultValue, options);
                }

                return {
                    id: field.fieldId,
                    title: field.fieldLabel,
                    type: field.fieldType,
                    tooltip: field.tooltip,
                    validation: getValidations(field, field.validators),
                    value: defaultValue,
                    options: options
                }
            });

            setStateFields(stateFields);
        }
    }

    const getValidations = (field, validators) => {
        const validations = [];
        validators?.forEach((validation) => {
            switch (validation) {
                case "min":
                    validations.push(["min", field.minValue]);
                    break;
                case "max":
                    validations.push(["max", field.maxValue]);
                    break;
                case "pattern":
                    validations.push(["pattern", field.pattern]);
                    break;
                default:
                    validations.push([validation]);
                    break;
            }
        });

        return validations;
    }

    const getDropdownOptions = (dropdownOptions) => {
        if (Array.isArray(dropdownOptions)) {
            return dropdownOptions.map((option) => {
                return {
                    value: option.id,
                    label: option.name
                };
            });
        }

        return null;
    }

    const setTaxDetailsData = (taxDetails) => {
        if (taxDetails?.id) {
            setIsEdit(true);
            setTaxDetails(taxDetails);
        }
    }

    const setTaxDetailsFieldData = () => {
        const fields = [...federalFields].map((field) => {
            switch (field.type) {
                case INPUT_TYPE.DROPDOWN:
                    return {...field, value: getDropdownLabel(taxDetails[field.id], field.options)};
                default:
                    return {...field, value: taxDetails[field.id]};
            }
        });

        setFederalFields(fields);

        if (taxDetails.compliance) {
            const fields = [...stateFields].map((field) => {
                switch (field.type) {
                    case INPUT_TYPE.DROPDOWN:
                        return {...field, value: getDropdownLabel(taxDetails.compliance[field.id], field.options)};
                    default:
                        return {...field, value: taxDetails.compliance[field.id]};
                }
            });

            setStateFields(fields);
        }        
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o?.findIndex(i => i.value === v);
        if (index !== -1 && o && o[index]) {
            value = o[index].label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const showError = (errorMessage) => {
        if (errorMessage) {
            return (
                <div style={{ marginTop: "-19px", marginBottom: "8px" }}>
                    <DKLabel className="ml-xs fs-s text-red" text={errorMessage} />
                </div>
            )
        }
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            for (let v of validation) {
                switch (v[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    case "noWhiteSpace":
                        if (value?.length > 0 && String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    case "min":
                        if (!isNaN(parseFloat(value)) && parseFloat(value) < v[1]) {
                            error.invalid = true;
                            error.errorMessage = `${field.title} should not less than ${v[1]}`;
                        }
                        break;
                    case "max":
                        if (!isNaN(parseFloat(value)) && parseFloat(value) > v[1]) {
                            error.invalid = true;
                            error.errorMessage = `${field.title} should not greater than ${v[1]}`;
                        }
                        break;
                    case "pattern":
                        if (value && !new RegExp(v[1]).test(value)) {
                            error.invalid = true;
                            error.errorMessage = `Invalid ${field.title}`;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }

                if (error.invalid) {
                    break;
                }
            }
        }

        return error;
    }

    const updateState = (section, field, value) => {
        let fields;
        let setFields;

        switch (section) {
            case "federal":
                fields = federalFields;
                setFields = setFederalFields;
                break;
            case "state":
                fields = stateFields;
                setFields = setStateFields;
                break;
            default:
                break;
        }

        fields = fields.map((f) => {
            if (f.id === field.id) {
                return {...f, value: value, ...hasError(f, value)};
            } else {
                return f;
            }
        });

        updateValueAndValidity(fields, field.id);

        setFields(fields);
    }

    const updateValueAndValidity = (fields, fieldId) => {

    }

    const isInvalid = () => {
        let invalid = false;

        const sections = {
            federal: federalFields,
            state: stateFields
        };

        Object.entries(sections).forEach(([key, state]) => {
            const fields = [...state];
            fields.forEach((field) => {
                const error = hasError(field, field.value);
                field.invalid = error.invalid;
                field.errorMessage = error.errorMessage;
    
                if (field.invalid) {
                    invalid = true;
                }
            });

            switch (key) {
                case "federal":
                    setFederalFields(fields);
                    break;
                case "state":
                    setStateFields(fields);
                    break;
                default:
                    break;
            }
        });

        return invalid;
    }

    const getPayload = () => {
        const payload = {
            stateCode: stateCode,
            compliance: null
        };

        if (isEdit) {
            payload["id"] = taxDetails.id;
        }

        federalFields.forEach((field) => {
            switch (field.type) {
                case INPUT_TYPE.DROPDOWN:
                    payload[field.id] = getDropdownValue(field.value, field.options);
                    break;
                default:
                    payload[field.id] = field.value;
                    break;
            }
        });

        if (stateFields?.length > 0) {
            switch (stateCode) {
                case "VT":
                    payload.compliance = {
                        "@type": "type.googleapis.com/v1.VTStateCompliance"
                    };
                    break;
                case "NH":
                    payload.compliance = {
                        "@type": "type.googleapis.com/v1.NHStateCompliance"
                    };
                    break;
                case "TX":
                    payload.compliance = {
                        "@type": "type.googleapis.com/v1.TXStateCompliance"
                    };
                    break;
                case "UT":
                    payload.compliance = {
                        "@type": "type.googleapis.com/v1.UTStateCompliance"
                    };
                    break;
                case "MN":
                    payload.compliance = {
                        "@type": "type.googleapis.com/v1.MNStateCompliance"
                    };
                    break;
                case "MA":
                    payload.compliance = {
                        "@type": "type.googleapis.com/v1.MAStateCompliance"
                    };
                    break;
                default:
                    break;
            }
    
            stateFields.forEach((field) => {
                switch (field.type) {
                    case INPUT_TYPE.DROPDOWN:
                        payload.compliance[field.id] = getDropdownValue(field.value, field.options);
                        break;
                    default:
                        payload.compliance[field.id] = field.value;
                        break;
                }
            });
        }


        return payload;
    }

    const save = () => {
        if (isInvalid()) {
            return;
        }

        const payload = getPayload();

        if (isEdit) {
            showLoader(t("UPDATE_TAX_DETAILS"));
            Table.updateUSTaxDetails(payload).then(
                (response) => {
                    removeLoader();
                    if (response?.id) {
                        showToast(t("UPDATE_TAX_SUCCESS"), TOAST_TYPE.SUCCESS);
                        updateStateOfProfileStage();
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }, (error) => {
                    removeLoader();
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            );
        } else {
            showLoader(t("SAVE_TAX_DETAILS"));
            Table.saveUSTaxDetails(payload).then(
                (response) => {
                    removeLoader();
                    if (response?.id) {
                        showToast(t("SAVE_TAX_SUCCESS"), TOAST_TYPE.SUCCESS);
                        updateStateOfProfileStage();
                        setIsEdit(true);

                        
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }, (error) => {
                    removeLoader();
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            );
        }
    }

    const updateStateOfProfileStage = () => {
        ProfileSetupService.updateStateOfProfileStage(PROFILE_STAGES_CONSTANT.TAX_DETAILS).then(
            (response) => {
                TenantManager.setProfileData(response['profileStages']);
                saveAndNext()
            },error=>{
                saveAndNext()
            }
        );
    }
    const saveAndNext = () => {
        if (props.renderAsStep) {
            props.onSaveAndNext();
        }
    }
    const renderField = (stateName, field, width) => {
        switch (field.type) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.NUMBER:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKInput
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || !PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT])}
                                required={isRequired(field.validation)}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "warning-tooltip-prop"
                                }}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                                validator={field.validator}
                            />
                        </div>
                        {field.invalid && showError(field.errorMessage)}
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: `0 0 ${width}`, maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                dropdownConfig={{
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label);
                                    },
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || !PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT])}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={() => {}}
                            />
                        </div>
                        {field.invalid && showError(field.errorMessage)}
                    </div>
                );
                case INPUT_TYPE.CHECKBOX:
                return (
                    <div key={field.id} className="row align-items-center" style={{ flex: `0 0 ${width}`, maxWidth: width }}>
                    {/* <div className="" > */}
                        <DKCheckMark
                            className="pt-s pb-l"
                            isSelected={field.value}
                            onClick={() => {
                                updateState(stateName, field, !field.value);
                                
                            }}
                        />
                        <DKLabel className="pt-s pb-l ml-s" text={field.title} />
                    {/* </div> */}
                    </div>
                )
            default:
                break;
        }
    }

    const renderFederalFields = () => {
        return (
            <div className="bg-white border-radius-m mt-l">
                <div className="row p-h-xxl">
                    <DKLabel text={t("FEDERAL_TAX_DETAILS")} className="fs-l fw-m pt-l pb-s" />
                </div>
                <DKLine className="bg-line-2 mb-m" />
                <div className="p-h-xxl" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
                    {federalFields.map((field) => renderField("federal", field, "48%"))}
                </div>
            </div>
        );
    }

    const renderStateFields = () => {
        return (
            <div className={`bg-white border-radius-m ${!props.renderAsStep && "mt-l"}`}>
                <DKLabel text={t("STATE_TAX_DETAILS")} className="fs-l fw-m p-h-xxl pt-l pb-s" />
                <DKLine className="bg-line-2 mb-m" />
                <div className="p-h-xxl" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
                    {stateFields.map((field) => renderField("state", field, "48%"))}
                </div>
            </div>
        );
    }

    const renderHeaders = () => {
        return (
            <div className="row">
                <DKLabel text={t("TAX_DETAILS")} className="fs-l fw-m"/>
                <div className="ml-auto d-flex">
                    <DKButton
                        title={t("CANCEL")}
                        className="border-radius-m bg-gray1 border-m mr-m"
                        onClick={() => {
                            RouteManager.navigateToHome();
                        }}
                    />
                    {PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT]) && <DKButton
                        title={t("SAVE")}
                        className="bg-app text-white"
                        onClick={() => {
                            save();
                        }}
                    />}
                </div>
            </div>
        );
    }

    const renderHeaderForStep = () => {
        return (
            <div className="row justify-content-between p-h-xxl p-v-m bg-gray2">
                <DKLabel text={t("TAX_DETAILS")} className="fs-l fw-m" />
                <DKButton
                    title={"Close"}
                    className="border-m bg-white ml-auto"
                    onClick={() => {
                        props.onClose();
                    }}
                />
                <DKButton
                    title={"Save & Next"}
                    className="border-m bg-app text-white ml-m"
                    onClick={() => {
                        save();
                    }}
                />
            </div>
        )
    }

    return (
        <div className="parent-width" style={{
            pointerEvents: PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT]) ? "":"none",
            opacity: PermissionUtils.hasAccess(MODULE.COMPANY_SETTINGS, [ACTION.EDIT]) ? "1":"0.9"
          }}>
            {!props.renderAsStep && renderHeaders()}
            {props.renderAsStep && renderHeaderForStep()}
            {renderFederalFields()}
            {stateFields?.length > 0 && renderStateFields()}
        </div>
    );
}

export default USTaxDetailsNew;
